import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {ContentContainer} from "@jane/lib/src/components/content/ContentContainer";
import {Breadcrumbs} from "../../../../lib/src/components/content/Breadcrumbs";
import {Input} from "../../../../lib/src/components/form/Input";
import moment from "moment/moment";
import {useNavigate, useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft, faPeopleGroup, faPerson, faSquareCheck, faUser} from "@fortawesome/free-solid-svg-icons";
import {useApiCall, useApiEndpoint} from "../../../../lib/src/api";
import {config} from "../../config";
import {faSquare} from "@fortawesome/free-regular-svg-icons";
import {LearnDashboardStatistic, LearnGraphData} from "../../../../lib/src/api/repositories/learn-repository";
import {Select} from "@jane/lib/src/components/form/Select";
import {MetricTile} from "../../components/learn/MetricTile";
import {LineGraph} from "../../components/learn/LineGraph";
import { getCategoryChildrenRecursively } from "@jane/lib/src/learn/services/modules";
import {faClipboardCheck} from "@fortawesome/pro-regular-svg-icons";
import {faCardsBlank, faDoorOpen, faPlay, faUserGraduate} from "@fortawesome/pro-solid-svg-icons";

export const timeOptions: { [key: string]: string } = {
  'day': "Per dag",
  'week': "Per week",
  'month': "Per maand",
  'quarter': "Per kwartaal",
  'year': "Per jaar",
}

export const LearnDashboardsProgramPage: FC<{}> = props => {
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from')
  const to = searchParams.get('to')
  const categories = searchParams.get('categories')

  const {learn} = useApiCall(config)
  const { resource: categoryResource, reload: reloadCategories} = useApiEndpoint(() => learn.listCategories())
  const { resource: moduleResource, reload: reloadModules } = useApiEndpoint(() => learn.listModules())

  const [fromDate, setFromDate] = useState<Date>(from ? moment(from, 'DD-MM-yyyy').toDate() : new Date(moment.now()))
  const [upToDate, setToDate] = useState<Date>(to ? moment(to, 'DD-MM-yyyy').toDate(): new Date(moment.now()))
  const [selectedCategories, setSelectedCategories] = useState<string[]>(categories ? categories.split(",") : []);
  const [statistics, setStatistics] = useState<LearnDashboardStatistic|null>(null)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true)
  const navigate = useNavigate()

  const primaryCategories = useMemo(() => {
    if (categoryResource === undefined) {
      return [];
    }

    return categoryResource.filter((cat) => cat.parent_category_id === null)
  }, [categoryResource])
  
  const moduleIds = useMemo(() => {
    if (categoryResource === undefined || moduleResource === undefined) {
      return [];
    }

    const selectedCategoryAndChildCategoryIds = selectedCategories.map((sc) => [
      sc,
      ...getCategoryChildrenRecursively(categoryResource, sc).map(c => c.id)
    ]).flat()

    return moduleResource
      .filter(mod => mod.category_id !== null && selectedCategoryAndChildCategoryIds.includes(mod.category_id))
      .map(mod => mod.id);
  }, [selectedCategories, categoryResource, moduleResource])

  const fetchStatistics = useCallback(() => {
    learn.fetchProgramDashboardStatistics(
      fromDate,
      upToDate,
      moduleIds
    ).then((result) => {
      setStatistics(result)
    })
  }, [fromDate, upToDate, moduleIds])

  useEffect(() => {
    if (moduleIds.length > 0) {
      fetchStatistics()
    }
  }, [fromDate, upToDate, moduleIds, fetchStatistics]);
  return <ContentContainer size={'full'}>
    <div className={'flex items-stretch bg-white border-b border-brand-200 -mx-6 -mt-8 h-20'}>
      <div className={'flex-1 px-8 flex flex-col justify-center'}>
        <div className={'mt-5 -mb-1'}>
          <Breadcrumbs currentPage={'Dashboards'} crumbs={[
            {label: 'Kennisbank', href: '/learn'}
          ]} />
        </div>
        <div className={'py-2 text-2xl font-bold'}>Kennisbank Dashboard</div>
      </div>

      <div className={'flex px-8 py-4 border-l border-brand-200 gap-x-6 items-center'}>
        <div className={'text-brand-900 font-bold mr-2'}>Periode:</div>
        <div className={'text-sm text-brand-primary font-semibold underline hover:cursor-pointer hover:text-red-500'} onClick={() => {
          setFromDate(new Date(moment().subtract(30, 'days').toDate()))
          setToDate(new Date(moment().toDate()))
        }}>30d</div>
        <div className={'text-sm text-brand-primary font-semibold underline hover:cursor-pointer hover:text-red-500'} onClick={() => {
          setFromDate(new Date(moment().subtract(90, 'days').toDate()))
          setToDate(new Date(moment().toDate()))
        }}>90d</div>
        <div className={'w-64'}>
          <Input inline={true} type={'date'} label={'Van:'} value={fromDate} onChange={setFromDate} />
        </div>
        <div className={'w-64'}>
          <Input inline={true} type={'date'} label={'Tot en met:'} value={upToDate} onChange={setToDate} />
        </div>
      </div>
    </div>

    <div className={"flex bg-white border-b border-b-red-200 -mx-6 px-6 py-4 space-x-3 items-center"}>
      <div>Programma(s):</div>

      <div className={"relative"}>
        <div className={'flex flex-row space-x-3'}>
          {categoryResource?.filter(cat => cat.parent_category_id === null)
            .map((category, i) => {
              const isChecked = selectedCategories.includes(category.id)
              return <div key={i} className={'flex flex-row items-center space-x-1 border-2 border-slate-200 rounded-sm py-1.5 px-2 hover:bg-brand-50 hover:cursor-pointer'}
                          onClick={() => {
                            if (isChecked) {
                              setSelectedCategories(prev => prev.filter((cat) => cat != category.id))
                            } else {
                              setSelectedCategories(prev => [...prev, category.id])
                            }
                          }}>
              <FontAwesomeIcon icon={isChecked ? faSquareCheck : faSquare} className={'text-brand-primary'} />
              <div>{category.name}</div>
            </div>
          })}
        </div>
      </div>
    </div>

    <StatisticsContent statistics={statistics} fromDate={fromDate} toDate={upToDate} moduleIds={moduleIds} />

  </ContentContainer>
}

const StatisticsContent: FC<{ statistics: LearnDashboardStatistic|null, fromDate: Date, toDate: Date, moduleIds: string[] }> = props => {
  return <div className={'mt-10 mx-6'}>
    <div className={'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-8'}>
      <MetricTile
        type={'numeric'}
        title={"Modules toegewezen"}
        subText={"Totaal aantal modules toegewezen binnen deze periode."}
        currentPeriod={props.statistics?.assigned_modules.this_period ?? 0}
        previousPeriod={props.statistics?.assigned_modules.previous_period ?? 0}
        icon={faCardsBlank}
      />

      <MetricTile
        type={'numeric'}
        title={"Gestarte modules"}
        subText={"Aantal modules waar medewerkers mee gestart zijn binnen deze periode."}
        currentPeriod={props.statistics?.started_modules.this_period ?? 0}
        previousPeriod={props.statistics?.started_modules.previous_period ?? 0}
        icon={faPlay}
      />

      <MetricTile
        type={'numeric'}
        title={"Afgeronde modules"}
        subText={"Aantal modules die medewerkers afgerond hebben binnen deze periode."}
        currentPeriod={props.statistics?.finished_modules.this_period ?? 0}
        previousPeriod={props.statistics?.finished_modules.previous_period ?? 0}
        icon={faClipboardCheck}
      />

      <MetricTile
        type={'numeric'}
        title={"Modules vervallen"}
        subText={"Aantal modules die vervallen zijn door uitdiensttreding binnen deze periode."}
        currentPeriod={props.statistics?.dropped_modules.this_period ?? 0}
        previousPeriod={props.statistics?.dropped_modules.previous_period ?? 0}
        icon={faDoorOpen}
      />

      <MetricTile
        type={'numeric'}
        title={"Uitgegeven certificaten"}
        subText={"Aantal certificaten die uitgegeven zijn binnen deze periode."}
        currentPeriod={props.statistics?.awarded_certificates.this_period ?? 0}
        previousPeriod={props.statistics?.awarded_certificates.previous_period ?? 0}
        icon={faUserGraduate}
      />

      <MetricTile
        type={'numeric'}
        title={"Medewerkers toegewezen"}
        subText={"Totaal aantal medewerkers toegewezen aan het programma."}
        currentPeriod={props.statistics?.employees_assigned.this_period ?? 0}
        previousPeriod={props.statistics?.employees_assigned.previous_period ?? 0}
        icon={faUser}
      />

      <MetricTile
        type={'numeric'}
        title={"Medewerkers gestart"}
        subText={"Totaal aantal medewerkers gestart aan het programma."}
        currentPeriod={props.statistics?.employees_started.this_period ?? 0}
        previousPeriod={props.statistics?.employees_started.previous_period ?? 0}
        icon={faPlay}
      />

      <MetricTile
        type={'numeric'}
        title={"Medewerkers afgerond"}
        subText={"Aantal medewerkers die de programma's hebben afgerond."}
        currentPeriod={props.statistics?.employees_finished.this_period ?? 0}
        previousPeriod={props.statistics?.employees_finished.previous_period ?? 0}
        icon={faClipboardCheck}
      />

      <MetricTile
        type={'numeric'}
        title={'Medewerkers uit dienst'}
        subText={'Aantal medewerkers die binnen deze periode uit dienst zijn getreden.'}
        currentPeriod={props.statistics?.employees_stopped.this_period ?? 0}
        previousPeriod={props.statistics?.employees_stopped.previous_period ?? 0}
        icon={faDoorOpen}
      />

    </div>
    <div className={'grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-8 mt-10'}>
      <ContinuedModulesGraphTile from={props.fromDate} upTo={props.toDate} moduleIds={props.moduleIds} />
      <StartedModulesGraphTile from={props.fromDate} upTo={props.toDate} moduleIds={props.moduleIds} />
      <OpenModulesGraphTile from={props.fromDate} upTo={props.toDate} moduleIds={props.moduleIds} />
      <ProgressGraphTile from={props.fromDate} upTo={props.toDate} moduleIds={props.moduleIds} />
    </div>
  </div>
}

const ProgressGraphTile: FC<{ from: Date, upTo: Date, moduleIds: string[] }> = props => {
  const {learn} = useApiCall(config)
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>(Object.keys(timeOptions)[3])
  const [data, setData] = useState<LearnGraphData|null>(null)

  const fetch = async () => {
    await learn.fetchProgressForGraph(
      props.from,
      props.upTo,
      props.moduleIds,
      selectedTimeOption
    ).then((result) => {
      setData(result)
    })
  }

  useEffect(() => {
    if (props.moduleIds.length > 0) {
      fetch()
    }
  }, [props.from, props.upTo, props.moduleIds])

  return <div className={'bg-brand-secondary-container rounded-xl px-6 py-5 col-span-1 flex flex-col'}>
        <div className={'flex justify-between mb-2'}>
          <div className={'flex flex-col'}>
          <div className={'text-brand-on-primary-container font-bold'}>Voortgang per kwartaal</div>
        <div className={'flex-1 text-sm text-brand-secondary'}>Momentopname van voortgang over alle modules binnen het programma per kwartaal</div>
      </div>
    </div>
    <div className={'flex-1'}></div>
    <div className={'bg-white rounded-sm'}>
      <LineGraph xAxisLabels={data ? Object.keys(data) : ['no data']}
                 yAxisValues={data ?? {}}
                 dataPointLabel={'% afgerond'}
                 yAxisConfiguration={'percentage'}
      />
  </div>
</div>
}

const OpenModulesGraphTile: FC<{ from: Date, upTo: Date, moduleIds: string[] }> = props => {
  const {learn} = useApiCall(config)
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>(Object.keys(timeOptions)[1])
  const [data, setData] = useState<LearnGraphData|null>(null)

  const fetch = async () => {
    await learn.fetchOpenModulesForGraph(
      props.from,
      props.upTo,
      props.moduleIds,
      selectedTimeOption
    ).then((result) => {
      setData(result)
    })
  }

  useEffect(() => {
    if (props.moduleIds.length > 0) {
      fetch()
    }
  }, [selectedTimeOption, props.from, props.upTo, props.moduleIds])

  return <div className={'bg-brand-secondary-container rounded-xl px-6 py-5 col-span-1 flex flex-col'}>
    <div className={'flex justify-between mb-2'}>
      <div className={'flex flex-col'}>
        <div className={'text-brand-on-primary-container font-bold'}>Openstaande modules per tijdvak</div>
        <div className={'flex-1 text-sm text-brand-secondary'}>Aantal gelijktijdig openstaande modules binnen het programma door de tijd heen</div>
      </div>
      <div>
        <Select label={''} options={timeOptions} value={selectedTimeOption} onChange={setSelectedTimeOption}/>
      </div>
    </div>
    <div className={'flex-1'}></div>
    <div className={'bg-white rounded-sm'}>
      <LineGraph xAxisLabels={data ? Object.keys(data) : ['no data']}
                 yAxisValues={data ?? {}}
                 dataPointLabel={'Modules'}
      />
    </div>
  </div>
}

const StartedModulesGraphTile: FC<{ from: Date, upTo: Date, moduleIds: string[] }> = props => {
  const {learn} = useApiCall(config)
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>(Object.keys(timeOptions)[1])
  const [data, setData] = useState<LearnGraphData|null>(null)

  const fetch = async () => {
    await learn.fetchStartedModulesForGraph(
      props.from,
      props.upTo,
      props.moduleIds,
      selectedTimeOption
    ).then((result) => {
      setData(result)
    })
  }

  useEffect(() => {
    if (props.moduleIds.length > 0) {
      fetch()
    }
  }, [selectedTimeOption, props.from, props.upTo, props.moduleIds])

  return <div className={'bg-brand-secondary-container rounded-xl px-6 py-5 col-span-1 flex flex-col'}>
    <div className={'flex justify-between mb-2'}>
      <div className={'flex flex-col'}>
        <div className={'text-brand-on-primary-container font-bold'}>Instroom per tijdvak</div>
        <div className={'flex-1 text-sm text-brand-secondary'}>Modules die medewerkers gestart hebben, per tijdvak</div>
      </div>
      <div>
        <Select label={''} options={timeOptions} value={selectedTimeOption} onChange={setSelectedTimeOption}/>

      </div>
    </div>
    <div className={'flex-1'}></div>
    <div className={'bg-white rounded-sm'}>
      <LineGraph xAxisLabels={data ? Object.keys(data) : ['no data']}
                 yAxisValues={data ?? {}}
                 dataPointLabel={'Modules'}
      />
    </div>
  </div>
}

const ContinuedModulesGraphTile: FC<{ from: Date, upTo: Date, moduleIds: string[] }> = props => {
  const {learn} = useApiCall(config)
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>(Object.keys(timeOptions)[1])
  const [data, setData] = useState<LearnGraphData|null>(null)

  const fetch = async () => {
    await learn.fetchContinuedModulesForGraph(
      props.from,
      props.upTo,
      props.moduleIds,
      selectedTimeOption
    ).then((result) => {
      setData(result)
    })
  }

  useEffect(() => {
    if (props.moduleIds.length > 0) {
      fetch()
    }
  }, [selectedTimeOption, props.from, props.upTo, props.moduleIds])

  return <div className={'bg-brand-secondary-container rounded-xl px-6 py-5 col-span-1 flex flex-col'}>
    <div className={'flex justify-between mb-2'}>
      <div className={'flex flex-col'}>
        <div className={'text-brand-on-primary-container font-bold'}>Doorlooptijd per tijdvak</div>
        <div className={'flex-1 text-sm text-brand-secondary'}>Gemiddelde doorlooptijd (in dagen) van modules per tijdvak</div>
      </div>
      <div>
        <Select label={''} options={timeOptions} value={selectedTimeOption} onChange={setSelectedTimeOption}/>

      </div>
    </div>
    <div className={'flex-1'}></div>
    <div className={'bg-white rounded-sm'}>
      <LineGraph xAxisLabels={data ? Object.keys(data) : ['no data']}
                 yAxisValues={Object.fromEntries(Object.entries(data ?? {}).map(([key, value]) => [key, Math.round(value / 8640) / 10]))} // value is converted from seconds to days
                 dataPointLabel={'dagen'}
      />
    </div>
  </div>
}
